<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'
import CustomDatatable from '@components/tables/custom-datatable'
import Multiselect from 'vue-multiselect'
import { 
  managementMastersMunicipalitiesMethods,
  managementMastersDepartmentsMethods,
  managementMastersTerritoriesMethods,
  managementMastersSourcesMethods,
  managementMastersLinesMethods,
  managementMastersProjectsMethods,
  managementMastersActivitiesMethods,
  managementMastersRequirementsMethods,
  managementMastersBanksMethods,
} from '@state/helpers'
import { mapGetters } from 'vuex'
import Vue from 'vue'
import store from '@state/store'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
const _ = require('lodash')


const CustomInputField = Vue.component('input-field', {
  components: { Multiselect, ValidationProvider, ValidationObserver },
  props: {
    objField: {
      type: [Object, String],
      required: true
    },
    objValue: {
      type: [Object, Number, String, Boolean, Array],
      required: false,
      default: null
    },
    formObject: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      newValue: null,
      isSubmitting: false,
      options: []
    }
  },
  methods: {
    async fetchLocalModel(route, model, query, search=''){
      this.isSubmitting = true
      const params = this.$select_params(search + query)
      const url = `${route}/fetch${model}`
      let res = await store.dispatch(url, params).then(async(res) => {
        this.isSubmitting = false
        this.options = res.results.map(x => ({'id': x.id, 'name': x.instance__name || x.name }))
        return res.results
      })
      return res
    },
    asyncFind: _.debounce(function(searchQuery) {
      this.fetchLocalModel(this.objField.model_route, this.objField.model_source, searchQuery)
    }, 600),
  },
  async beforeMount() {
    if(this.objField.type === 'model' || this.objField.type === 'array_model'){
      await this.fetchLocalModel(this.objField.model_route, this.objField.model_source, this.objField.model_query || '').then(async(res) => {
        if (this.objValue === null){
          this.newValue = null
          return true
        }
        if (this.objField.type === 'array_model'){
          this.newValue = await Promise.all(
            this.objValue.map(async(x) => 
              await {
                'id': x, 
                'name': res.find((y) => y.id === x).instance__name || res.find((y) => y.id === x).name 
              }
            )
          )
        } else { // 'model'
          this.newValue = this.objValue
          const search = res.find((y) => y.id === (this.objValue.id || this.objValue))
          if (search && Object(search).hasOwnProperty('instance__name') && search.instance__name !== null && search.instance__name !== ''){
            this.newValue = { id: search.id, name: search.instance__name || search.name }
          }
        }
      })
    } else {
      this.newValue = this.objValue
    }
    this.formObject[this.objField.key || this.objField] = this.newValue
  },
  watch: {
    newValue: {
      deep: true,
      immediate: true,
      handler: function (val, oldVal) {
        this.formObject[this.objField.key || this.objField] = val
      }
    },
  },
  template: `<ValidationProvider 
              v-slot="validationContext"
              :name="objField.key"
              :rules="objField.required ? 'required' : null">
              <div v-if="(typeof objField === 'string' && ['disabled','foreign__data','instance__name'].includes(objField)) || objField.visible === false" class="d-none"></div>
              <div v-else-if="objField.type === 'readonly'" class="text-muted">
                <b-form input v-model="newValue" type="hidden" :id="objField.key" :name="objField.key" :readonly="true" :value="objValue" /> {{ objValue }}
              </div>
              <div v-else-if="typeof objField === 'string' && objField === 'disabled'" class="d-none">
                <b-form-checkbox v-model="newValue" :id="objField.key" :autocomplete="objField.key" :name="objField.key" :checked="objValue" /> 
              </div>
              <div v-else-if="typeof objField === 'string'" class="text-muted">
                <b-form-input v-model="newValue" type="text" class="d-none" :id="objField.key" :autocomplete="objField.key" :name="objField.key" :value="objValue" />{{ objValue }}
              </div>
              <div v-else-if="objField.type === 'text'">
                <b-form-input v-model="newValue" type="text" :id="objField.key" :autocomplete="objField.key" :name="objField.key" :value="objValue" />
              </div>
              <div v-else-if="objField.type === 'textfield'">
                <b-form-textarea v-model="newValue" :id="objField.key" :autocomplete="objField.key" :name="objField.key" :value="objValue" />
              </div>
              <div v-else-if="objField.type === 'boolean'">
                <b-form-checkbox v-model="newValue" :id="objField.key" :autocomplete="objField.key" :name="objField.key" :checked="objValue === 1 ? true : objValue === 0 ? false : objValue" switch /> 
              </div>
              <div v-else-if="objField.type === 'model' || objField.type === 'array_model'">
                <Multiselect
                  v-model="newValue"
                  :id="objField.key"
                  :name="objField.key"
                  track-by="id"
                  :multiple="objField.type === 'array_model' ? true : false"
                  :loading="isSubmitting"
                  :options="options" 
                  :custom-label="opt => opt.name"
                  :internal-search="true"
                  @----search-change="asyncFind"
                  :select-label="$t('multiselect.select')"
                  :selected-label="$t('multiselect.selected')"
                  :deselect-label="$t('multiselect.deselect')"
                  :placeholder="$t('multiselect.placeholder')"
                  :showNoResults="true"
                  :showNoOptions="true"
                >
                  <span slot="noOptions">{{ $t('multiselect.no_options') }}</span>
                  <span slot="noResult">{{ $t('multiselect.no_result') }}</span>
                </Multiselect>
              </div>
              <b-form-invalid-feedback
                :state="validationContext.errors.length === 0"
              >
                {{ validationContext.errors.join('. ') }}
              </b-form-invalid-feedback>
            </ValidationProvider>`
})

export default {
  page: {
    title: 'Administration/Masters',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: { CustomDatatable, Layout, PageHeader, Multiselect, CustomInputField },
  data() {
      return {
        title: 'Administration/Masters',
        breadCrumb: [
          {
            text: 'Home',
            href: '/',
          },
          {
            text: 'Administration/Masters',
            active: true,
          },
        ],
        actionsColumn: { key: 'actions', label: this.$t('basic.actions'), class: 'w-100px' },
        masterList: [],
        activeMaster: null,
        isSubmitting: false,
        masterResults: null,
        masterColumns: [],
        editModal: { display: false, title: '', object: {} },
        createModal: { display: false, title: '', object: {} },
        deleteModal: { display: false, title: '', object: {} },
        formEdit: {},
        formCreate: {},
      }
  },
  computed: {
    ...mapGetters({
      municipalityFields: 'management/masters/municipalities/getMunicipalityFields',
      departmentFields: 'management/masters/departments/getDepartmentFields',
      territoryFields: 'management/masters/territories/getTerritoryFields',
      sourceFields: 'management/masters/sources/getSourceFields',
      lineFields: 'management/masters/lines/getLineFields',
      projectFields: 'management/masters/projects/getProjectFields',
      activityFields: 'management/masters/activities/getActivityFields',
      bankFields: 'management/masters/banks/getBankFields',
      requirementsFields: 'management/masters/requirements/getActivitySubtaskFields',
    }),
  },
  mounted () {
    // maestros disponibles en la lista a elegir:
    this.masterList = [
        this.departmentFields,
        this.municipalityFields,
        this.territoryFields,
        this.sourceFields,
        this.lineFields,
        this.projectFields,
        this.requirementsFields,
        this.activityFields,
        this.bankFields
      ]
  },
  methods: {
    ...managementMastersDepartmentsMethods,
    ...managementMastersMunicipalitiesMethods,
    ...managementMastersTerritoriesMethods,
    ...managementMastersSourcesMethods,
    ...managementMastersLinesMethods,
    ...managementMastersProjectsMethods,
    ...managementMastersActivitiesMethods,
    ...managementMastersRequirementsMethods,
    ...managementMastersBanksMethods,
    fetchData(){
      const label = this.activeMaster.label
      this.isSubmitting = true
      const ctx = this.$refs['table'] ?  this.$refs.table : null
      const data = this[`fetch${label}`](this.$table_params(ctx)).then(x => {
        this.isSubmitting = false
        if (x.results){
          this.masterResults = x.results
          this.translateFields((res) => {
            this.masterColumns = res
          })
        }
        return x
      }).catch(error => {
        this.$axios_error_noty(error, this)
        return []
      })
      return data
    },
    translateFields(){
      let new_fields = []
      this.activeMaster.fields.filter(x => x.visible !== false).forEach(field => {
        let item = {...field}
        item.label = this.$t('mastersSection.labels.'+item.label)
        new_fields.push(item)
      })
      this.masterColumns = new_fields.concat(this.actionsColumn)
    },
    getLabel(obj){
      if(typeof obj === "object"){
        return this.$t('mastersSection.labels.'+obj.label)
      } 
      return this.$t('mastersSection.labels.'+obj)
    },
    async submitEdit() {
      this.isSubmitting = true
      const isValid = await this.$refs.observer.validate();
      if (!isValid) {
        this.isSubmitting = false
        this.$generate_noty('warn', this.$t('forms.form_errors'), this.$t('alerts.error'),  3000, true)
        return
      }
      let form = _.cloneDeep(this.formEdit)
      for (const item of Object.entries(form)) {
        if (typeof item[1] === 'object' && !['foreign__data','instance__name'].includes(item[0])){
          try {
            if (this.activeMaster.fields.find(x => x.key === item[0]).type === 'array_model'){
              form[item[0]] = item[1].map(x => x.id)
            } else { // model
              form[item[0]] = item[1].id
            }
          } catch(e) {
            console.log("error:", e)
            this.$generate_noty('error', this.$t('forms.update_error'), this.$t('alerts.error'),  3000, true)
          }
        }
      }
      this[`update${this.activeMaster.label}`]({ payload: form }).then(x => {
        this.isSubmitting = false
        this.$generate_noty('success', this.$t('forms.row_updated'))
        this.editModal.display = false
        // this.fetchData()
        this.$refs['table'].refresh()
      }).catch(error => {
        this.isSubmitting = false
        this.$generate_noty('error', this.$t('forms.update_error'), this.$t('alerts.error'),  3000, true)
      })
    },
    async submitCreate() {
      this.isSubmitting = true
      const isValid = await this.$refs.observer2.validate();
      if (!isValid) {
        this.isSubmitting = false
        this.$generate_noty('warn', this.$t('forms.form_errors'), this.$t('alerts.error'),  3000, true)
        return
      }
      let form = _.cloneDeep(this.formCreate)
      for (const item of Object.entries(form)) {
        if (typeof item[1] === 'object' && item[1] !== null){
          if (this.activeMaster.fields.find(x => x.key === item[0]).type === 'array_model'){
            form[item[0]] = item[1].map(x => x.id)
          } else { // model
            form[item[0]] = item[1].id
          }
        } else if(item[1] === null){ // TODO: Empty values only strings!
          form[item[0]] = ''
        }
      }
      this[`create${this.activeMaster.label}`]({ payload: form }).then(x => {
        this.isSubmitting = false
        this.$generate_noty('success', this.$t('forms.row_created'))
        this.createModal.display = false
        // this.fetchData()
        this.$refs['table'].refresh()
      }).catch(error => {
        this.isSubmitting = false
        this.$generate_noty('error', this.$t('forms.create_error'), this.$t('alerts.error'),  3000, true)
      })
    },
    submitDelete() {
      this.isSubmitting = true
      this[`delete${this.activeMaster.label}`]({ id: this.deleteModal.object.id }).then(x => {
        this.isSubmitting = false
        this.$generate_noty('success', this.$t('forms.row_deleted'))
        this.deleteModal.display = false
        // this.fetchData()
        this.$refs['table'].refresh()
      }).catch(error => {
        this.isSubmitting = false
        this.$generate_noty('error', this.$t('forms.delete_error'), this.$t('alerts.error'),  3000, true)
      })
    },
    alertForeign(foreign_models) {
      const len = Object.values(foreign_models).reduce( (out, inp) => out + (inp.count || (inp.data && inp.data.length)), 0)
      let text = this.$tc('alerts.error_delete_with_dependencies', len, { count: len })
      foreign_models.forEach((x) => {
        if (Object(x).hasOwnProperty('data') && x.data.length > 0){
          text += `<br><br><b>- ${this.$t('mastersSection.masters.'+x.model)}</b>: `
          text += x.data.join(', ')
        } else if (Object(x).hasOwnProperty('count') && x.count > 0){
          text += `<br><br><b>- ${this.$t('mastersSection.masters.'+x.model)}</b>: `
          text += x.count + ' ' + this.$t('datatable.rows')
        }
      })
      this.$generate_noty('warning', text, 'Atención')
    },
    setActiveMaster(key, modalObject){
      const obj = this.activeMaster.fields.find(x => x.key === key).model_source
      this.activeMaster = this.masterList.find(x => x.label === obj)
      modalObject.display = false
    },
    getObjDefinition(obj){
      return this.activeMaster.fields.find(x => x.key === obj) || obj
    }
  },
  watch: {
    activeMaster: {
      deep: true,
      immediate: true,
      handler: function (val, oldVal) {
        if(val && val.label){
          this.isSubmitting = true
          this.fetchData()
        }
      }
    },
    '$i18n.locale': function(newVal, oldVal) {
      this.translateFields((res) => {
        this.masterColumns = res
      })
    }
  },
}
</script>

<template>
  <Layout>
    <PageHeader
      :title="title"
      :items="breadCrumb"
    />
    <!-- edition modal: -->
    <b-modal
      v-model="editModal.display"
      :cancel-title="$t('basic.cancel')"
      :title="editModal.title"
      hide-footer
      >
      <b-overlay :show="isSubmitting">
        <ValidationObserver ref="observer" @submit.prevent="submitEdit">
          <b-form ref="editform" v-if="editModal.display" >
            <b-row v-for="(obj, idx) in Object.keys(editModal.object)" :key="idx" class="mb-2" v-show="!['disabled', 'foreign__data','instance__name'].includes(obj) && (getObjDefinition(obj)).visible !== false">
              <b-col md="4">
                <div v-if="(getObjDefinition(obj).type === 'model' || getObjDefinition(obj).type === 'array_model') && masterList.map(x => x.label).includes(getObjDefinition(obj).model_source)" >
                  <b-link @click="setActiveMaster(obj, editModal)" active class="text-warning text-decoration-underline" v-b-tooltip.hover :title="$t('mastersSection.go_to_master')">
                    {{ getLabel(getObjDefinition(obj)) }}
                  </b-link>
                </div>
                <div v-else>
                  {{ getLabel(getObjDefinition(obj)) }}
                </div>
              </b-col>
              <b-col md="8">
                <CustomInputField 
                  :formObject="formEdit" :objField="getObjDefinition(obj)" 
                  :objValue="editModal.object[obj]" />
              </b-col>
            </b-row>
              <div class="w-100">
                <b-button
                  type="submit"
                  :disabled="isSubmitting"
                  variant="primary"
                  size="sm"
                  class="float-right"
                >
                  {{ $t('basic.submit') }}
                </b-button>
              </div>
          </b-form>
        </ValidationObserver>
      </b-overlay>
    </b-modal>
    <!-- create modal: -->
    <b-modal
      v-model="createModal.display"
      :cancel-title="$t('basic.cancel')"
      :title="createModal.title"
      hide-footer
      >
      <b-overlay :show="isSubmitting">
        <ValidationObserver ref="observer2" @submit.prevent="submitCreate">
          <b-form ref="createform" v-if="createModal.display" >
            <b-row v-for="(obj, idx) in activeMaster.fields" :key="idx" class="mb-2">
              <b-col md="4">{{ getLabel(getObjDefinition(obj)) }}</b-col>
              <b-col md="8"><CustomInputField :formObject="formCreate" :objField="getObjDefinition(obj)" :objValue="null" /></b-col>
            </b-row>
              <div class="w-100">
                <b-button
                  type="submit"
                  :disabled="isSubmitting"
                  variant="primary"
                  size="sm"
                  class="float-right"
                >
                  {{ $t('basic.submit') }}
                </b-button>
              </div>
          </b-form>
        </ValidationObserver>
      </b-overlay>
    </b-modal>
    <!-- delete modal: -->
    <b-modal
      v-model="deleteModal.display"
      :title="deleteModal.title"
      :ok-title="$t('basic.delete')"
      :cancel-title="$t('basic.cancel')"
      ok-variant="danger"
      @cancel="deleteModal.display = false"
      @ok="submitDelete"
      >
      <b-overlay :show="isSubmitting">
        <p>
          {{ $t('mastersSection.are_you_sure') }} <b>ID: {{ deleteModal.object.id }}</b>?
        </p>
        <template v-slot:modal-footer="{ ok, cancel }">
          <b-button size="sm" variant="secondary" @click="cancel">
            {{ $t('basic.cancel') }}
          </b-button>
          <b-button
            size="sm"
            variant="danger"
            :disabled="isSubmitting"
            @click="ok">
            <span
              v-if="!isSubmitting"
              >
              {{ $t('delete') }}
            </span>
            <b-spinner
              v-if="isSubmitting"
              small
              label="Spinning"
              variant="light"
              >
            </b-spinner>
          </b-button>
        </template>
      </b-overlay>
    </b-modal>
    <!-- main content (menu and table): -->
    <div class="row">
      <div class="col-md-12">
        <div class="p-2">
          <b-row>
            <b-col xs="12" md="4" lg="4" xl="3">
              <label>{{ $t('mastersSection.select_master') }}</label>
            </b-col>
            <b-col xs="12" md="8" lg="5" xl="4">
              <Multiselect
                id="perms-select"
                v-model="activeMaster"
                :multiple="false"
                :loading="isSubmitting"
                :options="masterList" 
                :custom-label="opt => $t('mastersSection.masters.'+opt.label)"
                :internal-search="true"
                :select-label="$t('multiselect.select')"
                :selected-label="$t('multiselect.selected')"
                :deselect-label="$t('multiselect.deselect')"
                :placeholder="$t('multiselect.placeholder')"
                :showNoResults="true"
                :showNoOptions="true"
                >
                <span slot="noOptions">{{ $t('multiselect.no_options') }}</span>
                <span slot="noResult">{{ $t('multiselect.no_result') }}</span>
              </Multiselect>
            </b-col>
          </b-row>
        </div>
        <b-card v-if="activeMaster">
          <b-row>
            <b-col cols="7">
              <h3 class="mb-3">{{ $t('mastersSection.masters.'+activeMaster.label) }}</h3>
            </b-col>
            <b-col cols="5" class="text-right">
              <b-button v-if="activeMaster && !isSubmitting" variant="primary" @click="createModal.display = true; createModal.title = $t('basic.new')+' '+$t('mastersSection.row')+' | '+$t('mastersSection.masters.'+activeMaster.label);">
                <i class="fad fa-plus"></i> {{ $t('basic.add') }}
              </b-button>
            </b-col>
          </b-row>
          <CustomDatatable
            v-if="activeMaster"
            ref="table"
            :key="activeMaster.label"
            :drfprovider="fetchData"
            :tableheaders="masterColumns"
            :perPageOptions="true" 
            :hover="true"
          >
            <template #cell(actions)="data">
              <b-button-group size="xs">
                <b-button
                  variant="transparent-1"
                  class="text-warning p-1"
                  :disabled="isSubmitting"
                  @click="editModal.display = true; editModal.title = $t('basic.edit')+' '+$t('mastersSection.row'); editModal.object = data.item"
                  >
                  <i v-if="!isSubmitting" class="fad fa-pen fe-2x"></i>
                  <b-spinner
                    v-if="isSubmitting"
                    small
                    label="Spinning"
                    variant="warning"
                    >
                  </b-spinner>
                </b-button>
                <b-button
                  variant="transparent-1"
                  class="text-danger p-1"
                  :disabled="isSubmitting"
                  @click="(data.item.foreign__data && data.item.foreign__data.length > 0 && data.item.foreign__data && data.item.foreign__data.filter(x => ((x.data && x.data.length) || x.count) > 0).length > 0)  ? alertForeign(data.item.foreign__data) : deleteModal.display = true; deleteModal.title = $t('basic.delete')+' '+$t('mastersSection.row'); deleteModal.object = data.item"
                  >
                  <i v-if="!isSubmitting" class="fad fa-trash-xmark"></i>
                  <b-spinner
                    v-if="isSubmitting"
                    small
                    label="Spinning"
                    variant="danger"
                    >
                  </b-spinner>
                </b-button>                  
              </b-button-group>
            </template>
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner class="align-middle"></b-spinner><br>
                <strong>{{ $t('datatable.loading') }}</strong>
              </div>
            </template>
          </CustomDatatable>          
        </b-card>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss">
.w-100px { width: 100px; }
</style>